import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { authService } from "../../services/authService";
import {
  subscribeToVotingProjects,
  createVotingProject,
  VotingProject,
} from "../../services/votingService";
import { db } from "../../utils/firebase";
import { updateDoc, deleteDoc, doc, serverTimestamp } from "firebase/firestore";
import VotingProjectForm from "./VotingProjectForm";
import VotingActivity from "./VotingActivity";

const VotingAdmin: React.FC = () => {
  const [activeTab, setActiveTab] = useState<
    "overview" | "projects" | "activity"
  >("overview");
  const [projects, setProjects] = useState<VotingProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingProject, setEditingProject] = useState<VotingProject | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = authService.onAuthStateChanged((user) => {
      if (user) {
        setUser(user);
      } else {
        navigate("/admin/login");
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Subscribe to real-time project updates
  useEffect(() => {
    const unsubscribe = subscribeToVotingProjects((updatedProjects) => {
      setProjects(updatedProjects);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    try {
      await authService.signOut();
      navigate("/admin/login");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const handleDeleteProject = async (id: string) => {
    if (
      window.confirm("Are you sure you want to delete this voting project?")
    ) {
      try {
        const projectRef = doc(db, "votingProjects", id);
        await deleteDoc(projectRef);
      } catch (error) {
        console.error("Error deleting project:", error);
        alert("Failed to delete project. Please try again.");
      }
    }
  };

  const handleEditProject = (project: VotingProject) => {
    setEditingProject(project);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingProject(null);
  };

  const handleFormSubmit = async (projectData: {
    name: string;
    group: string;
    poster?: string;
    youtubeUrl?: string;
  }) => {
    try {
      if (editingProject) {
        // Update existing project
        const projectRef = doc(db, "votingProjects", editingProject.id);
        await updateDoc(projectRef, {
          ...projectData,
          updatedAt: serverTimestamp(),
        });
      } else {
        // Create new project
        await createVotingProject(projectData);
      }
      setShowForm(false);
      setEditingProject(null);
    } catch (error) {
      console.error("Error saving project:", error);
      alert("Failed to save project. Please try again.");
    }
  };

  // Calculate statistics
  const totalVotes = projects.reduce((sum, p) => sum + (p.voteCount || 0), 0);
  const totalProjects = projects.length;
  const topProject = projects.reduce((top, p) =>
    (p.voteCount || 0) > (top.voteCount || 0) ? p : top,
    projects[0] || { voteCount: 0 }
  );

  return (
    <div className="w-full min-h-screen bg-[var(--background-primary)] text-[var(--text-primary)]">
      {/* Header */}
      <header className="bg-[var(--background-primary)] border-b border-[var(--border-color)] shadow-sm sticky top-0 z-50">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-bold bg-gradient-to-r from-[var(--gradient-primary)] to-[var(--gradient-secondary)] bg-clip-text text-transparent">
              Padma Voting Admin
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-[var(--text-secondary)]">
              {user?.email || "Admin"}
            </span>
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors duration-200 text-sm font-medium"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="max-w-[1400px] mx-auto px-6 py-4 border-b border-[var(--border-color)]">
        <div className="flex gap-4">
          <button
            onClick={() => setActiveTab("overview")}
            className={`px-6 py-2 rounded-lg transition-all duration-200 font-medium ${
              activeTab === "overview"
                ? "bg-[var(--gradient-primary)] text-white"
                : "bg-[var(--background-primary)] text-[var(--text-secondary)] hover:bg-[var(--background-primary)] hover:text-[var(--text-primary)]"
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab("projects")}
            className={`px-6 py-2 rounded-lg transition-all duration-200 font-medium ${
              activeTab === "projects"
                ? "bg-[var(--gradient-primary)] text-white"
                : "bg-[var(--background-primary)] text-[var(--text-secondary)] hover:bg-[var(--background-primary)] hover:text-[var(--text-primary)]"
            }`}
          >
            Projects
          </button>
          <button
            onClick={() => setActiveTab("activity")}
            className={`px-6 py-2 rounded-lg transition-all duration-200 font-medium ${
              activeTab === "activity"
                ? "bg-[var(--gradient-primary)] text-white"
                : "bg-[var(--background-primary)] text-[var(--text-secondary)] hover:bg-[var(--background-primary)] hover:text-[var(--text-primary)]"
            }`}
          >
            Activity
          </button>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-[1400px] mx-auto px-6 py-6">
        {loading && activeTab === "overview" ? (
          <div className="text-center py-12">
            <div className="text-[var(--text-secondary)]">Loading...</div>
          </div>
        ) : (
          <>
            {/* Overview Tab */}
            {activeTab === "overview" && (
              <div className="space-y-6">
                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-[rgba(255,255,255,0.95)] dark:bg-[rgba(30,41,59,0.95)] backdrop-blur-[20px] rounded-xl p-6 shadow-lg border border-[rgba(59,130,246,0.2)]">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-[var(--text-secondary)] mb-1">
                          Total Votes
                        </p>
                        <p className="text-3xl font-bold text-[var(--text-primary)]">
                          {totalVotes}
                        </p>
                      </div>
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[var(--gradient-primary)] to-[var(--gradient-secondary)] flex items-center justify-center">
                        <i className="fa-solid fa-heart text-white text-xl"></i>
                      </div>
                    </div>
                  </div>

                  <div className="bg-[rgba(255,255,255,0.95)] dark:bg-[rgba(30,41,59,0.95)] backdrop-blur-[20px] rounded-xl p-6 shadow-lg border border-[rgba(59,130,246,0.2)]">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-[var(--text-secondary)] mb-1">
                          Total Projects
                        </p>
                        <p className="text-3xl font-bold text-[var(--text-primary)]">
                          {totalProjects}
                        </p>
                      </div>
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center">
                        <i className="fa-solid fa-folder text-white text-xl"></i>
                      </div>
                    </div>
                  </div>

                  <div className="bg-[rgba(255,255,255,0.95)] dark:bg-[rgba(30,41,59,0.95)] backdrop-blur-[20px] rounded-xl p-6 shadow-lg border border-[rgba(59,130,246,0.2)]">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-[var(--text-secondary)] mb-1">
                          Top Project Votes
                        </p>
                        <p className="text-3xl font-bold text-[var(--text-primary)]">
                          {topProject?.voteCount || 0}
                        </p>
                        {topProject?.name && (
                          <p className="text-xs text-[var(--text-secondary)] mt-1">
                            {topProject.name}
                          </p>
                        )}
                      </div>
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center">
                        <i className="fa-solid fa-trophy text-white text-xl"></i>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Projects List */}
                <div className="bg-[rgba(255,255,255,0.95)] dark:bg-[rgba(30,41,59,0.95)] backdrop-blur-[20px] rounded-xl shadow-lg border border-[rgba(59,130,246,0.2)]">
                  <div className="p-6 border-b border-[var(--border-color)]">
                    <h2 className="text-xl font-bold text-[var(--text-primary)]">
                      All Projects
                    </h2>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-[var(--background-primary)]">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider">
                            Project Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider">
                            Group
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider">
                            Votes
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider">
                            Created
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[var(--border-color)]">
                        {projects.length === 0 ? (
                          <tr>
                            <td
                              colSpan={5}
                              className="px-6 py-12 text-center text-[var(--text-secondary)]"
                            >
                              No projects found. Create your first project!
                            </td>
                          </tr>
                        ) : (
                          projects
                            .sort((a, b) => (b.voteCount || 0) - (a.voteCount || 0))
                            .map((project) => (
                              <tr
                                key={project.id}
                                className="hover:bg-[var(--background-primary)] transition-colors"
                              >
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex items-center gap-3">
                                    {project.poster ? (
                                      <img
                                        src={project.poster}
                                        alt={project.name}
                                        className="w-12 h-12 rounded-lg object-cover"
                                      />
                                    ) : (
                                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[rgba(37,99,235,0.1)] to-[rgba(14,165,233,0.1)] flex items-center justify-center">
                                        <i className="fa-solid fa-image text-[var(--text-secondary)]"></i>
                                      </div>
                                    )}
                                    <span className="font-medium text-[var(--text-primary)]">
                                      {project.name}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-[var(--text-secondary)]">
                                  {project.group}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[rgba(239,68,68,0.1)] text-[#ef4444] font-semibold">
                                    <i className="fa-solid fa-heart"></i>
                                    {project.voteCount || 0}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-[var(--text-secondary)] text-sm">
                                  {project.createdAt
                                    ? new Date(
                                        project.createdAt.toMillis()
                                      ).toLocaleDateString()
                                    : "N/A"}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                  <div className="flex items-center justify-end gap-2">
                                    <button
                                      onClick={() => handleEditProject(project)}
                                      className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-xs font-medium"
                                    >
                                      <i className="fa-solid fa-edit mr-1"></i>
                                      Edit
                                    </button>
                                    <button
                                      onClick={() =>
                                        handleDeleteProject(project.id)
                                      }
                                      className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-xs font-medium"
                                    >
                                      <i className="fa-solid fa-trash mr-1"></i>
                                      Delete
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Projects Tab */}
            {activeTab === "projects" && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-[var(--text-primary)]">
                    Manage Projects
                  </h2>
                  <button
                    onClick={() => setShowForm(true)}
                    className="px-6 py-3 bg-gradient-to-r from-[var(--gradient-primary)] to-[var(--gradient-secondary)] hover:from-[var(--gradient-secondary)] hover:to-[var(--gradient-primary)] text-white rounded-lg transition-all duration-200 font-medium shadow-lg"
                  >
                    <i className="fa-solid fa-plus mr-2"></i>
                    Add New Project
                  </button>
                </div>

                {/* Projects Table */}
                <div className="bg-[rgba(255,255,255,0.95)] dark:bg-[rgba(30,41,59,0.95)] backdrop-blur-[20px] rounded-xl shadow-lg border border-[rgba(59,130,246,0.2)]">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-[var(--background-primary)]">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider">
                            Project
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider">
                            Group
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider">
                            Votes
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-[var(--text-secondary)] uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[var(--border-color)]">
                        {projects.length === 0 ? (
                          <tr>
                            <td
                              colSpan={5}
                              className="px-6 py-12 text-center text-[var(--text-secondary)]"
                            >
                              No projects found. Create your first project!
                            </td>
                          </tr>
                        ) : (
                          projects.map((project) => (
                            <tr
                              key={project.id}
                              className="hover:bg-[var(--background-primary)] transition-colors"
                            >
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-3">
                                  {project.poster ? (
                                    <img
                                      src={project.poster}
                                      alt={project.name}
                                      className="w-16 h-16 rounded-lg object-cover"
                                    />
                                  ) : (
                                    <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-[rgba(37,99,235,0.1)] to-[rgba(14,165,233,0.1)] flex items-center justify-center">
                                      <i className="fa-solid fa-image text-[var(--text-secondary)] text-xl"></i>
                                    </div>
                                  )}
                                  <div>
                                    <div className="font-medium text-[var(--text-primary)]">
                                      {project.name}
                                    </div>
                                    <div className="text-sm text-[var(--text-secondary)]">
                                      Created:{" "}
                                      {project.createdAt
                                        ? new Date(
                                            project.createdAt.toMillis()
                                          ).toLocaleDateString()
                                        : "N/A"}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 text-[var(--text-secondary)]">
                                {project.group}
                              </td>
                              <td className="px-6 py-4">
                                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[rgba(239,68,68,0.1)] text-[#ef4444] font-semibold">
                                  <i className="fa-solid fa-heart"></i>
                                  {project.voteCount || 0}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 text-xs font-medium">
                                  Active
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <div className="flex items-center justify-end gap-2">
                                  <button
                                    onClick={() => handleEditProject(project)}
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-xs font-medium"
                                  >
                                    <i className="fa-solid fa-edit mr-1"></i>
                                    Edit
                                  </button>
                                  <button
                                    onClick={() =>
                                      handleDeleteProject(project.id)
                                    }
                                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-xs font-medium"
                                  >
                                    <i className="fa-solid fa-trash mr-1"></i>
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Activity Tab */}
            {activeTab === "activity" && (
              <VotingActivity projects={projects} />
            )}
          </>
        )}
      </main>

      {/* Project Form Modal */}
      {showForm && (
        <VotingProjectForm
          project={editingProject}
          onClose={handleFormClose}
          onSubmit={handleFormSubmit}
        />
      )}
    </div>
  );
};

export default VotingAdmin;

